import { useCallback, useEffect, useState } from 'react';
import { settingsApi } from '@/features/ai-dart/api/settings';
import { DartFormat } from '@/features/ai-dart/format';
import type { IngestKey, IngestKeyCreated } from '@/features/ai-dart/types';
import { ActionButton, ActiveDot, TABLE, TD, TH, THEAD, TR } from '@/features/ai-dart/components/controls';
import { ConfirmModal, Modal } from '@/shared/ui/Modal';
import { Field, FormActions, FormError, Input } from '@/features/ai-dart/components/form';

export function IngestKeysView(): React.JSX.Element {
  const [keys, setKeys] = useState<IngestKey[]>([]); const [endpoint, setEndpoint] = useState('');
  const [created, setCreated] = useState<IngestKeyCreated | null>(null); const [message, setMessage] = useState('');
  const load = useCallback(() => settingsApi.ingestKeys.list().then((data) => { setKeys(data.keys); setEndpoint(data.endpoint); }).catch((error: Error) => setMessage(error.message)), []);
  useEffect(() => { void load(); }, [load]);

  const [issuing, setIssuing] = useState(false);        // 발급 모달 열림
  const [label, setLabel] = useState('');
  const [revoking, setRevoking] = useState<IngestKey | null>(null);
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const openIssue = (): void => { setFormError(''); setLabel('monitoring'); setIssuing(true); };
  const issue = async (): Promise<void> => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setBusy(true); setFormError('');
    try { setCreated(await settingsApi.ingestKeys.create(trimmed)); setIssuing(false); setLabel(''); load(); }
    catch (error) { setFormError((error as Error).message); }
    finally { setBusy(false); }
  };
  const revoke = async (key: IngestKey): Promise<void> => {
    setBusy(true);
    try { await settingsApi.ingestKeys.revoke(key.id); setRevoking(null); setMessage(`${key.label} 키를 폐기했습니다.`); load(); }
    catch (error) { setMessage((error as Error).message); }
    finally { setBusy(false); }
  };
  /** 클립보드는 권한·비보안 컨텍스트에서 거부된다 — 실패를 삼키지 않고 직접 복사하라고 알린다. */
  const copy = async (): Promise<void> => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { setMessage('클립보드에 복사할 수 없습니다 — 아래 값을 직접 선택해 복사하세요.'); }
  };

  const example = `curl -X POST -H 'Authorization: Bearer <KEY>' -H 'Content-Type: application/json' \\\n  -d '{"native_id":"evt-1","title":"...","service_hint":"nd-prod/nd-market-api"}' ${endpoint ? `${endpoint}/ingest` : '(endpoint 미설정)'}`;
  return (
    <section className="rounded-lg border border-line bg-surface">
      <header className="flex items-start gap-3 border-b border-line p-4">
        <div>
          <h2 className="text-[13px] font-bold">인입 키</h2>
          <p className="text-[11px] text-rose-700">이 키는 IAP를 우회하는 외부 시스템용 자격증명입니다. 최소한으로 발급하고 유출·미사용 시 즉시 아래 폐기 버튼으로 무효화하세요.</p>
        </div>
        <div className="ml-auto">
          <ActionButton tone="primary" onClick={openIssue}>＋ 키 발급</ActionButton>
        </div>
      </header>
      {created && (
        <div className="border-b border-line bg-amber-50 p-3 px-4 text-[12px]">
          <strong>평문 키는 지금 한 번만 표시됩니다.</strong>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded border border-amber-200 bg-white p-2">{created.key}</code>
            <ActionButton onClick={() => void copy()}>{copied ? '복사됨' : '복사'}</ActionButton>
            <ActionButton onClick={() => setCreated(null)}>닫기</ActionButton>
          </div>
          <div className="mt-1 text-[11px] text-muted">라벨: {created.label} · 접두어: {created.keyPrefix}</div>
        </div>
      )}
      {message && <p className="border-b border-line bg-line-soft p-2 px-4 text-[12px]">{message}</p>}
      <div className="border-b border-line bg-line-soft p-3 px-4 text-[11px]">
        <strong>호출 예시</strong>
        <pre className="mt-1 overflow-auto whitespace-pre-wrap">{example}</pre>
      </div>
      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th className={TH}>상태</th>
              <th className={TH}>라벨</th>
              <th className={TH}>접두어</th>
              <th className={TH}>발급</th>
              <th className={TH}>최근 사용</th>
              <th className={`${TH} text-right`}>조작</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr key={key.id} className={TR}>
                <td className={TD}><ActiveDot on={key.status === 'active'} label={key.status === 'active' ? '활성' : '폐기됨'} /></td>
                <td className={`${TD} font-medium`}>{key.label}</td>
                <td className={`${TD} font-mono`}>{key.keyPrefix}…</td>
                <td className={`${TD} whitespace-nowrap text-faint`}>{DartFormat.kst(key.createdAt)}</td>
                <td className={`${TD} whitespace-nowrap text-faint`}>{DartFormat.kst(key.lastUsedAt)}</td>
                <td className={TD}>
                  <div className="flex justify-end gap-1.5">
                    {key.status === 'active' && (
                      <ActionButton tone="danger" disabled={busy} onClick={() => setRevoking(key)}>폐기</ActionButton>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {keys.length === 0 && <tr><td className="p-8 text-center text-muted" colSpan={6}>발급된 키가 없습니다.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={issuing} onClose={() => setIssuing(false)} title="인입 키 발급">
        <Field label="라벨 (어디에 쓰는 키인지)" hint="목록과 사용 기록에서 이 이름으로 구분합니다.">
          <Input
            autoFocus
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void issue(); }}
            placeholder="예: datadog-monitor"
          />
        </Field>
        {formError && <FormError message={formError} />}
        <FormActions>
          <ActionButton tone="primary" disabled={busy || !label.trim()} onClick={() => void issue()}>발급</ActionButton>
          <ActionButton onClick={() => setIssuing(false)}>취소</ActionButton>
        </FormActions>
      </Modal>

      <ConfirmModal
        open={revoking !== null}
        title="인입 키 폐기"
        confirmLabel="폐기"
        busy={busy}
        onClose={() => setRevoking(null)}
        onConfirm={() => { if (revoking) void revoke(revoking); }}
      >
        <strong>{revoking?.label}</strong>(<span className="font-mono text-[11.5px]">{revoking?.keyPrefix}…</span>) 키를 폐기합니다.
        이 키로 들어오던 인입이 즉시 401이 되고, <strong>되돌릴 수 없습니다</strong> — 다시 쓰려면 새로 발급해야 합니다.
      </ConfirmModal>
    </section>
  );
}
